/**
 * TypeScript mirror of the backend's `template` field model (WP3 contract). These types and
 * helpers are pure — no React, no network — so a room, an overlay page, and an export job can all
 * share the exact same formatting/parsing logic as the Java side.
 *
 * Discriminated on `type` (same literal names as `FieldType`) to match the backend's Jackson
 * `@JsonTypeInfo(..., property = "type")` wire shape byte-for-byte.
 */

export type FieldType =
  | 'TIER'
  | 'STARS'
  | 'DURATION'
  | 'RATIO'
  | 'NUMBER'
  | 'SHORT_TEXT'
  | 'LONG_TEXT'
  | 'IMAGE'
  | 'LINK';

/** Which room rows a field is collected for. `ROOM` = one value per room, no Player/Community split. */
export type AppliesTo = 'BOTH_ROWS' | 'PLAYER_ONLY' | 'COMMUNITY_ONLY' | 'ROOM';

/** Which row a given `field_value` belongs to (WP4). */
export type RowKind = 'PLAYER' | 'COMMUNITY' | 'ROOM';

// ---------------------------------------------------------------------------
// FieldConfig — per-field-type configuration, as returned in `Field.config`.
// ---------------------------------------------------------------------------

export interface TierConfig {
  type: 'TIER';
  tiers: string[];
}

/** `labels[n]` names the rating for `n` filled stars (e.g. `labels[4]` is the label for 4/6 stars). */
export interface StarsConfig {
  type: 'STARS';
  max: number;
  labels: string[];
}

export interface DurationConfig {
  type: 'DURATION';
  maxSeconds: number;
}

export interface RatioConfig {
  type: 'RATIO';
  leftLabel: string;
  rightLabel: string;
}

/**
 * Decimals travel as strings over the wire (matches the backend's `BigDecimal` fields). Any of
 * `min`/`max`/`step` may be `null` — the backend emits `null` when that bound isn't configured for
 * the field, it does not always send a value.
 */
export interface NumberConfig {
  type: 'NUMBER';
  min: string | null;
  max: string | null;
  step: string | null;
}

export interface ShortTextConfig {
  type: 'SHORT_TEXT';
  maxLength: number;
}

export interface LongTextConfig {
  type: 'LONG_TEXT';
  maxLength: number;
}

export interface ImageConfig {
  type: 'IMAGE';
}

export interface LinkConfig {
  type: 'LINK';
}

export type FieldConfig =
  | TierConfig
  | StarsConfig
  | DurationConfig
  | RatioConfig
  | NumberConfig
  | ShortTextConfig
  | LongTextConfig
  | ImageConfig
  | LinkConfig;

// ---------------------------------------------------------------------------
// FieldValue — what's actually stored/exchanged for a field (WP4's `field_value.value` JSONB).
// ---------------------------------------------------------------------------

export interface TierValue {
  type: 'TIER';
  tier: string;
  footnote: boolean;
  note: string | null;
}

export interface StarsValue {
  type: 'STARS';
  stars: number;
  footnote: boolean;
  note: string | null;
}

export interface DurationValue {
  type: 'DURATION';
  minSeconds: number;
  maxSeconds: number | null;
  footnote: boolean;
  note: string | null;
}

export interface RatioValue {
  type: 'RATIO';
  left: number;
  right: number;
  footnote: boolean;
  note: string | null;
}

export interface NumberValue {
  type: 'NUMBER';
  value: string;
  footnote: boolean;
  note: string | null;
}

export interface ShortTextValue {
  type: 'SHORT_TEXT';
  text: string;
  footnote: boolean;
  note: string | null;
}

export interface LongTextValue {
  type: 'LONG_TEXT';
  text: string;
  footnote: boolean;
  note: string | null;
}

export interface ImageValue {
  type: 'IMAGE';
  imageAssetId: number;
  alt: string | null;
}

export interface LinkValue {
  type: 'LINK';
  url: string;
  label: string | null;
}

export type FieldValue =
  | TierValue
  | StarsValue
  | DurationValue
  | RatioValue
  | NumberValue
  | ShortTextValue
  | LongTextValue
  | ImageValue
  | LinkValue;

/** Appends `" *"` to a display string when the value carries a footnote. */
function withFootnote(display: string, footnote: boolean): string {
  return footnote ? `${display} *` : display;
}

/**
 * Renders a `FieldValue` the way the sheet/UI shows it: `"S"` / `"S *"` (TIER), `"★★★★☆☆ A"`
 * (STARS — glyphs plus the `starsLabel`, always present; the backend clamps `stars` into `0..max`
 * before formatting, so this mirrors that), `"15-20s"` / `"20s"` / `"30s *"` (DURATION), `"60/40"`
 * (RATIO), the raw text/value for NUMBER/SHORT_TEXT/LONG_TEXT, `"[image]"` for IMAGE (regardless of
 * `alt`), and the label for LINK, falling back to the URL when the label is `null` or blank.
 */
export function formatFieldValue(value: FieldValue, config: FieldConfig): string {
  switch (value.type) {
    case 'TIER':
      return withFootnote(value.tier, value.footnote);
    case 'STARS': {
      if (config.type !== 'STARS') {
        throw new Error(`formatFieldValue: STARS value with ${config.type} config`);
      }
      const glyphs = starsGlyphs(value.stars, config.max);
      const label = starsLabel(value.stars, config);
      return withFootnote(`${glyphs} ${label}`, value.footnote);
    }
    case 'DURATION':
      return withFootnote(formatDuration(value.minSeconds, value.maxSeconds), value.footnote);
    case 'RATIO':
      return withFootnote(`${value.left}/${value.right}`, value.footnote);
    case 'NUMBER':
      return withFootnote(value.value, value.footnote);
    case 'SHORT_TEXT':
    case 'LONG_TEXT':
      return withFootnote(value.text, value.footnote);
    case 'IMAGE':
      return '[image]';
    case 'LINK':
      return value.label && value.label.trim() !== '' ? value.label : value.url;
  }
}

function starsGlyphs(stars: number, max: number): string {
  const filled = Math.max(0, Math.min(stars, max));
  return '★'.repeat(filled) + '☆'.repeat(max - filled);
}

/**
 * The label naming an `n`-star rating for a given `STARS` config (`config.labels[n]`), clamping
 * `stars` into `0..max` first — same as the backend, which guarantees `labels.length === max + 1`
 * and clamps before formatting. Never returns `''` for a valid config; out-of-range input (e.g. `7`
 * with `max: 6`, or `-1`) resolves to the nearest in-range label (`"SSS"`, `"F"`) rather than being
 * treated as missing.
 */
export function starsLabel(stars: number, config: StarsConfig): string {
  const clamped = Math.max(0, Math.min(stars, config.max));
  return config.labels[clamped] ?? '';
}

function formatDuration(minSeconds: number, maxSeconds: number | null): string {
  if (maxSeconds !== null && maxSeconds !== minSeconds) {
    return `${minSeconds}-${maxSeconds}s`;
  }
  return `${minSeconds}s`;
}

/**
 * Parses a duration display string (`"15-20s"`, `"20s"`, `"30s *"`) back into its parts, or
 * `null` when the input doesn't match the sheet's format or describes a descending range
 * (`"20-15s"`).
 */
export function parseDuration(
  input: string,
): { minSeconds: number; maxSeconds: number | null; footnote: boolean } | null {
  const match = /^(\d+)(?:-(\d+))?s( \*)?$/.exec(input.trim());
  if (!match) {
    return null;
  }
  const minText = match[1];
  if (minText === undefined) {
    return null;
  }
  const maxText = match[2];
  const minSeconds = Number(minText);
  const maxSeconds = maxText !== undefined ? Number(maxText) : null;
  if (maxSeconds !== null && maxSeconds < minSeconds) {
    return null;
  }
  return { minSeconds, maxSeconds, footnote: match[3] !== undefined };
}

/**
 * Parses a ratio display string (`"60/40"`) back into its parts, or `null` when the input isn't
 * two non-negative integers summing to 100 (e.g. `"70/40"`).
 */
export function parseRatio(input: string): { left: number; right: number } | null {
  const match = /^(\d+)\/(\d+)$/.exec(input.trim());
  if (!match) {
    return null;
  }
  const leftText = match[1];
  const rightText = match[2];
  if (leftText === undefined || rightText === undefined) {
    return null;
  }
  const left = Number(leftText);
  const right = Number(rightText);
  if (left + right !== 100) {
    return null;
  }
  return { left, right };
}

/**
 * True when a `FieldValue` represents "nothing entered yet" for its type: `null`/`undefined`
 * altogether, a blank tier, empty text/URL, an unset NUMBER, or an unset IMAGE asset. STARS,
 * DURATION, and RATIO always carry a concrete rating once the value object exists (0 stars is a
 * real "F" rating, not an absence), so they're never considered empty here.
 */
export function isEmptyValue(value: FieldValue | null | undefined): boolean {
  if (value == null) {
    return true;
  }
  switch (value.type) {
    case 'TIER':
      return value.tier.trim() === '';
    case 'NUMBER':
      return value.value.trim() === '';
    case 'SHORT_TEXT':
    case 'LONG_TEXT':
      return value.text.trim() === '';
    case 'IMAGE':
      return !value.imageAssetId;
    case 'LINK':
      return value.url.trim() === '';
    case 'STARS':
    case 'DURATION':
    case 'RATIO':
      return false;
  }
}
